import React, { useState } from "react";
import { api } from "../utils/api";
import { useApp } from "../context/AppContext";
import { useToast } from "../components/Toast";
import { useI18n } from "../context/I18nContext";
import { isPhotoImage } from "../utils/images";

export default function IngredientSuggest({ navigate }) {
  const [input, setInput] = useState("");
  const [ingredients, setIngredients] = useState([]);
  const [results, setResults] = useState(null);
  const [aiAnswer, setAiAnswer] = useState("");
  const [aiLimitInfo, setAiLimitInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const { actions } = useApp();
  const { toast } = useToast();
  const { t, language } = useI18n();

  const exactMatches = (results || []).filter((meal) => meal.canMake);
  const partialMatches = (results || []).filter((meal) => !meal.canMake);

  const addIngredient = () => {
    const value = input.trim().toLowerCase();
    if (!value || ingredients.includes(value)) return;
    setIngredients((previous) => [...previous, value]);
    setInput("");
  };

  const removeIngredient = (ingredient) =>
    setIngredients((previous) => previous.filter((item) => item !== ingredient));

  const handleSearch = async () => {
    if (!ingredients.length) return;
    setLoading(true);
    setAiAnswer("");
    setAiLimitInfo(null);
    try {
      const response = await api.suggestByIngredients({ ingredients, lang: language, ...actions.usageProfile() });
      setResults(response.data);
    } catch (error) {
      toast(`${t("requestFailed")}: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAiSuggest = async () => {
    if (!ingredients.length || aiLoading) return;
    setAiLoading(true);
    setAiLimitInfo(null);
    try {
      const response = await api.suggestByIngredientsAi({
        ingredients,
        lang: language,
        ...actions.usageProfile(),
      });
      setAiAnswer(response.answer);
      if (response.data) setResults(response.data);
    } catch (error) {
      if (error?.code === "LIMIT_REACHED") {
        setAiLimitInfo(error.usage || {});
      } else {
        toast(`${t("requestFailed")}: ${error.message}`, "error");
      }
    } finally {
      setAiLoading(false);
    }
  };

  const addMissingToShopping = async (missingItems) => {
    let added = 0;
    for (const item of missingItems) {
      const result = await actions.addToShopping(item);
      if (result.success) added++;
    }
    toast(`${added} ${t("ingredientsAddedToShopping")}`, "success");
  };

  const clearAll = () => {
    setIngredients([]);
    setResults(null);
    setAiAnswer("");
    setAiLimitInfo(null);
  };

  return (
    <div>
      <div className="page-header">
        <h1>{t("ingredientSuggest")}</h1>
        <p>{t("ingredientSuggestIntro")}</p>
      </div>

      <div className="ingredient-input-area">
        <h3 style={{ marginBottom: 12, fontSize: 16, fontWeight: 600 }}>
          {t("yourIngredients")}
        </h3>

        <div className="ingredient-input-row">
          <input
            type="text"
            placeholder={t("addIngredientPlaceholder")}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && addIngredient()}
          />
          <button className="btn btn-primary" onClick={addIngredient}>+ {t("add")}</button>
        </div>

        {ingredients.length > 0 && (
          <div className="ingredient-tags">
            {ingredients.map((ingredient) => (
              <span key={ingredient} className="ingredient-tag">
                {ingredient}
                <button onClick={() => removeIngredient(ingredient)}>x</button>
              </span>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
          <button className="btn btn-primary" onClick={handleSearch} disabled={ingredients.length === 0 || loading}>
            {loading ? t("searching") : t("findRecipe")}
          </button>
          <button className="btn btn-outline" onClick={handleAiSuggest} disabled={ingredients.length === 0 || aiLoading}>
            {aiLoading ? t("answerPreparing") : t("aiIngredientHelp")}
          </button>
          {ingredients.length > 0 && (
            <button className="btn btn-ghost" onClick={clearAll}>
              {t("clear")}
            </button>
          )}
        </div>

        {aiLimitInfo && (
          <div className="reward-box" style={{ marginTop: 14 }}>
            <strong>{t("ingredientAiLocked")}</strong>
            <span>Günlük AI kullanım hakkın gece 00.00'da yenilenir.</span>
          </div>
        )}
      </div>

      {aiAnswer && (
        <div className="ai-suggestion-box">
          <strong>{t("aiIngredientResult")}</strong>
          <p>{aiAnswer}</p>
        </div>
      )}

      {loading && (
        <div className="loading"><div className="spinner" /><p>{t("searchingRecipes")}</p></div>
      )}

      {results !== null && !loading && (
        <>
          <p style={{ fontSize: 13, color: "var(--text3)", marginBottom: 16 }}>
            {results.length} {t("recipesFound")}
          </p>

          {results.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">-</div>
              <h3>{t("noMatchingRecipe")}</h3>
              <p>{t("tryDifferentIngredients")}</p>
            </div>
          ) : (
            <>
              {exactMatches.length > 0 && <h2 className="suggest-section-title">{t("exactMatches")}</h2>}
              <div className="meals-grid">
                {exactMatches.map((meal, index) => (
                  <SuggestCard
                    key={meal.id}
                    meal={meal}
                    index={index}
                    navigate={navigate}
                    actions={actions}
                    addMissingToShopping={addMissingToShopping}
                    t={t}
                  />
                ))}
              </div>

              {partialMatches.length > 0 && <h2 className="suggest-section-title">{t("partialMatches")}</h2>}
              <div className="meals-grid">
                {partialMatches.map((meal, index) => (
                  <SuggestCard
                    key={meal.id}
                    meal={meal}
                    index={index}
                    navigate={navigate}
                    actions={actions}
                    addMissingToShopping={addMissingToShopping}
                    t={t}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function SuggestCard({ meal, index, navigate, actions, addMissingToShopping, t }) {
  const openDetail = async () => {
    try {
      navigate("detail", meal);
    } catch (error) {
      window.alert(error.message || t("requestFailed"));
    }
  };

  return (
    <div className="suggest-card" style={{ animationDelay: `${index * 40}ms` }}>
      <div style={{ padding: "16px 16px 0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--text3)", marginBottom: 4 }}>
          <span>{t("match")}</span>
          <strong style={{ color: meal.canMake ? "var(--green)" : "var(--orange)" }}>
            %{Math.round(meal.matchRatio * 100)}
          </strong>
        </div>
        <div className="match-bar">
          <div className="match-fill" style={{ width: `${meal.matchRatio * 100}%` }} />
        </div>
      </div>

      <div style={{ padding: "12px 16px", cursor: "pointer" }} onClick={openDetail}>
        <div className={`suggest-photo ${isPhotoImage(meal.image) ? "has-photo" : ""}`}>
          {isPhotoImage(meal.image) ? <img src={meal.image} alt={meal.title} /> : meal.image}
        </div>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 17, fontWeight: 600, marginBottom: 8 }}>
          {meal.title}
        </div>
        <div style={{ fontSize: 13, color: "var(--text3)" }}>
          {meal.matched.length}/{meal.ingredients.length} {t("availableIngredients")}
        </div>
      </div>

      {meal.missing.length > 0 && (
        <div style={{ padding: "0 16px 16px" }}>
          <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 6, fontWeight: 600 }}>
            {t("missingIngredients")}
          </div>
          <div className="missing-list">
            {meal.missing.map((missing) => (
              <span key={missing} className="missing-item">{missing}</span>
            ))}
          </div>
          <button
            className="btn btn-green btn-sm"
            style={{ marginTop: 10, width: "100%" }}
            onClick={(event) => { event.stopPropagation(); addMissingToShopping(meal.missing); }}
          >
            {t("addMissingToList")}
          </button>
        </div>
      )}
    </div>
  );
}
